import './search-bar.scss';
import classNames from 'classnames';
import Icon from '../icons/icon';
import { debounce } from 'lodash';
import {
  Dispatch,
  InputHTMLAttributes,
  ReactNode,
  SetStateAction,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import { Typography } from '../typography/typography';

export type SearchBarProps<T extends { keyword?: string }> = Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & {
  defaultValue?: string;
  icon?: JSX.Element | ReactNode;
  subtitle?: string;
  setRequest: Dispatch<SetStateAction<T>>;
  inputClassName?: string; // Not sure why the input is set to a fixed with for md, so adding this in to change that
  minCharacters?: number; // Minimum characters required for search (if provided, shows hint)
};

export const SearchBar = forwardRef(
  <T extends { keyword?: string }>(
    {
      defaultValue,
      icon = <Icon className="ml-[12px]" icon="Search" color="quipli-black-500" />,
      placeholder = 'Search',
      className,
      setRequest,
      subtitle,
      inputClassName,
      minCharacters,
      ...props
    }: SearchBarProps<T>,
    ref: any,
  ) => {
    const [keyword, setKeyword] = useState<string>(defaultValue || '');
    const handleOnChangeKeyword = useCallback(
      debounce((keyword) => setRequest((prevState) => ({ ...prevState, keyword: keyword })), 500),
      [],
    );

    useEffect(() => {
      if (keyword) {
        handleOnChangeKeyword(keyword);
      } else {
        handleOnChangeKeyword(undefined);
      }
    }, [keyword]);

    // Set keyword from the outside
    useImperativeHandle(
      ref,
      () => {
        return {
          setKeyword(keyword: string) {
            setKeyword(keyword);
          },
        };
      },
      [],
    );

    const showMinCharacterMessage = minCharacters && keyword.length > 0 && keyword.length < minCharacters;

    return (
      <div className={classNames(className)}>
        <div className={classNames('search-bar w-full md:w-[548px]', inputClassName)}>
          {icon}
          <input
            ref={ref}
            type="text"
            placeholder={placeholder}
            onChange={({ target }) => setKeyword(target.value)}
            {...props}
            value={keyword}
          />
        </div>
        {subtitle && (
          <Typography type="text-xs" weight="regular" className="text-[var(--grey-500)]">
            {subtitle}
          </Typography>
        )}
        {showMinCharacterMessage && (
          <Typography type="text-xs" weight="regular" className="text-[var(--grey-500)] mt-1">
            Type at least {minCharacters} characters to search
          </Typography>
        )}
      </div>
    );
  },
);
